import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Reveal } from "@/components/landing/Shared";
import AdaptiveLoopHeader from "@/components/adaptive-loop/AdaptiveLoopHeader";

const SubHero = ({
  breadcrumb = [],
  overline,
  headline,
  headlineAccent,
  lead,
  showAdaptiveLoop = false,
  loopTestId,
  testId = "sub-hero",
}) => {
  return (
    <section
      data-testid={testId}
      className="relative pt-24 sm:pt-32 pb-16 sm:pb-20 border-b overflow-hidden forr-canvas"
      style={{ borderColor: "var(--forr-line-soft)" }}
    >
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] forr-radial-teal opacity-70 pointer-events-none" />
      <div className="absolute top-40 right-[-200px] w-[500px] h-[500px] forr-radial-warm opacity-40 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        {breadcrumb.length > 0 && (
          <Reveal>
            <nav
              data-testid={`${testId}-breadcrumb`}
              aria-label="Breadcrumb"
              className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] forr-text-mute mb-10"
            >
              {breadcrumb.map((b, i) => (
                <React.Fragment key={i}>
                  {b.to ? (
                    <Link
                      to={b.to}
                      className="hover:forr-text-teal-deep transition-colors"
                    >
                      {b.label}
                    </Link>
                  ) : (
                    <span className="forr-text-soft">{b.label}</span>
                  )}
                  {i < breadcrumb.length - 1 && (
                    <ChevronRight
                      size={12}
                      strokeWidth={1.5}
                      className="forr-text-faint"
                    />
                  )}
                </React.Fragment>
              ))}
            </nav>
          </Reveal>
        )}

        <div
          className={
            showAdaptiveLoop
              ? "grid grid-cols-12 gap-10 lg:gap-16 items-center"
              : ""
          }
        >
          <div className={showAdaptiveLoop ? "col-span-12 lg:col-span-8" : ""}>
            {overline && (
              <Reveal>
                <div className="inline-flex items-center gap-3 mb-8">
                  <span className="forr-hairline" />
                  <span
                    data-testid={`${testId}-overline`}
                    className="forr-overline"
                  >
                    {overline}
                  </span>
                </div>
              </Reveal>
            )}

            <Reveal delay={0.05}>
              <h1
                data-testid={`${testId}-headline`}
                className="font-display forr-text-ink text-[38px] leading-[1.04] sm:text-5xl md:text-6xl lg:text-7xl font-medium tracking-[-0.03em] max-w-4xl"
              >
                {headline}
                {headlineAccent && (
                  <>
                    {" "}
                    <span className="forr-text-teal-deep">{headlineAccent}</span>
                  </>
                )}
              </h1>
            </Reveal>

            {lead && (
              <Reveal delay={0.15}>
                <p
                  data-testid={`${testId}-lead`}
                  className="mt-8 max-w-2xl text-base sm:text-lg font-light forr-text-soft leading-relaxed"
                >
                  {lead}
                </p>
              </Reveal>
            )}
          </div>

          {showAdaptiveLoop && (
            <div className="col-span-12 lg:col-span-4 flex justify-center lg:justify-end">
              <Reveal delay={0.1} className="w-full max-w-[350px]">
                <AdaptiveLoopHeader
                  testId={loopTestId || `${testId}-adaptive-loop`}
                />
              </Reveal>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SubHero;
